import React from 'react';
import { motion } from 'framer-motion';
import { CalendarDaysIcon, CreditCardIcon, FileTextIcon, LockIcon, ShieldCheckIcon, TargetIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { ColombiaFlag } from '../ColombiaFlag';
import { Highlight } from '../Highlight';
import { heroBadges, heroStats, productImage, trustRow } from '../../data/landing';
const iconMap: Record<string, React.ReactNode> = {
  file: <FileTextIcon className="h-4 w-4 text-success" data-id="element-158" data-mp="mtirlpemh5kl" />,
  zap: <ZapIcon className="h-4 w-4 text-success" data-id="element-159" data-mp="qv3zsxg7ovog" />,
  target: <TargetIcon className="h-4 w-4 text-success" data-id="element-160" data-mp="qtj7zfla7qua" />,
  calendar: <CalendarDaysIcon className="h-4 w-4 text-success" data-id="element-161" data-mp="vndpqdhbtvqq" />,
  shield: <ShieldCheckIcon className="h-4 w-4 text-success" data-id="element-162" data-mp="10lfffh99ctb6" />,
  lock: <LockIcon className="h-4 w-4 text-success" data-id="element-163" data-mp="10knmke71p9ol" />,
  card: <CreditCardIcon className="h-4 w-4 text-success" data-id="element-164" data-mp="15l1ttu0toflk" />
};
export function Hero() {
  return <header className="relative overflow-hidden bg-ink px-5 pb-16 pt-6 md:pb-24 md:pt-16" data-id="element-165" data-mp="1ajd4u82usw5d">
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center" data-id="element-166" data-mp="9nswtp4hi3d5">
        <p className="order-1 flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.2em] text-body sm:text-sm md:text-base" data-id="element-167" data-mp="9lg8mu84uwrn">
          <ColombiaFlag className="h-4 w-6" data-id="element-168" data-mp="3vu94nb28y2g9" />
          Aprende y Emprende Online™
        </p>

        <h1 className="order-2 mt-3 text-4xl font-black leading-[1] tracking-tight text-heading sm:text-6xl md:mt-6 md:text-8xl" data-id="element-169" data-mp="3rtxoaagx2oal">
          Dopamina Cero
          <span className="mt-1 block md:mt-2" data-id="element-170" data-mp="sgepk0k3hdti">
            <Highlight data-id="element-171" data-mp="nexsyh8n8diq">en 7 Días.</Highlight>
          </span>
        </h1>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="order-3 mx-auto mt-4 flex w-full max-w-[250px] justify-center sm:max-w-[360px] md:mt-10 md:max-w-[460px]" data-id="element-172" data-mp="io9130t1xej2">
          <img src={productImage} width={549} height={442} alt="MegaPack Dopamina Cero: 6 tomos, 6 bonos y Protocolo PRND" className="mx-auto block h-auto w-full object-contain" data-id="element-173" data-mp="16d0kp3sksok8" />
        </motion.div>

        <p className="order-4 mt-4 max-w-3xl text-lg font-extrabold leading-snug text-heading md:mt-8 md:text-2xl" data-id="element-174" data-mp="35jz074vqqscx">
          El sistema N°1 para recuperar tu atención y concentración — Días 1 → 30 — Enfoque
          definitivo
        </p>
        <p className="order-5 mt-2 max-w-2xl text-sm text-body sm:text-base md:mt-3 md:text-lg" data-id="element-175" data-mp="2vsuihxeoktee">
          Guias para usar + paso a paso + checklist para usar. Sin buscar. Sin improvisar. Sin volver a caer en el scroll.
        </p>

        <ul className="order-7 mt-5 flex flex-wrap justify-center gap-2 md:mt-6" data-id="element-176" data-mp="27xrrpjwbgfva">
          {heroBadges.map(b => <li key={b.label} className="flex items-center gap-2 rounded-full border border-line bg-surface px-4 py-2 text-sm font-semibold text-heading" data-id="element-177" data-mp="2cvteaesyaxbi">
              {iconMap[b.icon]}
              {b.label}
            </li>)}
        </ul>

        <div className="order-6 mt-4 flex w-full justify-center md:mt-6" data-id="element-178" data-mp="b46tiuznjynh">
          <CtaButton href="#bonos" data-id="element-179" data-mp="1vo25a7nwixke">Quiero mi MegaPack ahora</CtaButton>
        </div>

        <ul className="order-8 mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-body" data-id="element-180" data-mp="1qop8ov78ac4m">
          {trustRow.map(t => <li key={t.label} className="flex items-center gap-2" data-id="element-181" data-mp="25eere4bd6ysn">
              {iconMap[t.icon]}
              {t.label}
            </li>)}
        </ul>

        <dl className="order-9 mt-12 grid w-full max-w-4xl grid-cols-2 overflow-hidden rounded-2xl border border-line bg-surface md:grid-cols-4" data-id="element-182" data-mp="2ecz0qev1bw47">
          {heroStats.map((s, i) => <div key={s.label} className={`px-4 py-6 ${i < 2 ? 'border-b border-line md:border-b-0' : ''} ${i % 2 === 0 ? 'border-r border-line' : ''} ${i === 1 ? 'md:border-r' : ''}`} data-id="element-183" data-mp="2ae9d7tga8y6u" data-mpi={i}>
              <dt className="sr-only" data-id="element-184" data-mp="2jaie7cjws4v3">{s.label}</dt>
              <dd className="text-3xl font-black text-heading md:text-4xl" data-id="element-185" data-mp="twvgrqvgftk2">{s.value}</dd>
              <dd className="mt-1 text-sm font-medium text-body" data-id="element-186" data-mp="oz3lcmvlppb4">{s.label}</dd>
            </div>)}
        </dl>
      </div>
    </header>;
}